import React, { useCallback, useEffect, useRef } from 'react'
import InfiniteList from 'components/InfiniteList'
import { useFlag } from 'constants/flags'
import { GetVersionHistoryQuery, VersionListItemFragment } from 'gqlTypes'
import { ShareVersionItem } from './ShareVersionItem'
import { ShareVersionDeletedItem } from './ShareVersionDeletedItem'
import { ShareVersionUnavailable } from './ShareVersionUnavailable'
import { Text } from 'components/Box'
import { SidebarSection } from 'modules/shares/components/ShareMetaSidebar/ShareMetaSidebar.styles'
import { SharePresenceItem } from 'modules/presence/components'

/**
 * Types
 */
export type VersionHistoryEntry = NonNullable<
  GetVersionHistoryQuery['share']
>['versionHistory']['entries'][0]

export interface ShareVersionSwitcherProps {
  entries: VersionHistoryEntry[]
  totalCount: number
  onLoadMore(): Promise<any>
  onLoadLatest(): Promise<any>
  shareIdentifier: string
  versionShortId: string
  shareName: string
  isViewingLatestVersion: boolean
  isLibrary: boolean
  canEdit: boolean
}

const getFirstVersion = (
  versions: VersionHistoryEntry[],
  allVersionsLoaded: boolean
): string | undefined => {
  if (!allVersionsLoaded) return undefined

  return versions.filter(version => version.__typename === 'Version').length > 1
    ? versions.find(version => version.__typename === 'Version')?.identifier
    : undefined
}

/**
 * Component
 */
export const ShareVersionSwitcher = ({
  entries,
  totalCount,
  onLoadMore,
  onLoadLatest,
  shareIdentifier,
  versionShortId,
  shareName,
  isViewingLatestVersion,
  isLibrary,
  canEdit,
}: ShareVersionSwitcherProps) => {
  const versionEndRef = useRef<HTMLDivElement>(null)
  const isCollabEnabled = useFlag('collaborative-editing')
  const isPresenceEnabled = useFlag('presence-v2')
  const canSeePresences = isCollabEnabled && isPresenceEnabled
  const sidebarSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onLoadLatest()
    // we need to call `onLoadLatest` only when the component was mounted.
    // we don't need to call it when props have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollToLatestVersion = useCallback(() => {
    // Scrolls down when viewing the last version
    if (isViewingLatestVersion) {
      window.requestAnimationFrame(() => {
        if (!versionEndRef.current?.scrollIntoView) return

        versionEndRef.current.scrollIntoView()
      })
    }
  }, [isViewingLatestVersion])

  async function loadMore() {
    const sidebarSection = sidebarSectionRef.current

    if (!sidebarSection) return onLoadMore()

    // Avoiding to loose the scroll position, when new items are prepended
    const height = sidebarSection.scrollHeight

    await onLoadMore()

    // if the scrollTop is 0, and we prepend items into a div,
    // the browser will remains at scrollTop 0, and this means that the
    // elements currently visible by the user will be pushed down by the new ones
    // This does not happen when the scrollTop is not 0
    if (sidebarSection.scrollTop < 1)
      sidebarSection.scrollTop = sidebarSection.scrollHeight - height
  }

  useEffect(() => {
    scrollToLatestVersion()
  }, [isViewingLatestVersion, scrollToLatestVersion])

  const savedEntries = [...entries].reverse()

  // getting all the stars from the document
  const stars = savedEntries.filter(
    savedEntry => 'kind' in savedEntry && savedEntry.kind === 'PUBLISHED'
  )
  const latestStar =
    stars.length > 0
      ? (stars[stars.length - 1] as VersionListItemFragment)
      : undefined

  // Versions are reversed to match the same display order as in the
  // Artboard panel
  return (
    <>
      <SidebarSection
        paddingLeft={1}
        paddingRight={1}
        dark
        borderTop
        innserSectionBottom>
        <Text.H3
          color="gray"
          fontSize={11}
          fontWeight="semibold"
          marginTop={0}
          marginBottom={0}
          marginLeft={4}
          textTransform="uppercase"
          lineHeight={1}>
          Updates ({totalCount})
        </Text.H3>
      </SidebarSection>
      <SidebarSection
        ref={sidebarSectionRef}
        paddingLeft={1}
        paddingRight={1}
        paddingTop={5}
        minHeight={92}
        dark
        scroll>
        <InfiniteList
          reversed={true}
          canLoadMore={entries.length < totalCount}
          onLoadMore={loadMore}>
          {savedEntries.map(version => {
            switch (version.__typename) {
              case 'Version': {
                const versionItem = (
                  <ShareVersionItem
                    key={version.shortId}
                    version={version}
                    shareIdentifier={shareIdentifier}
                    versionShortId={versionShortId}
                    shareName={shareName}
                    totalCount={totalCount}
                    kind={version.kind}
                    latestStar={latestStar}
                    isLibrary={isLibrary}
                    canEdit={canEdit}
                    downloadEnabled={
                      (version.document?.url &&
                        version.document?.downloadAvailable) ||
                      false
                    }
                    isFirstVersion={
                      getFirstVersion(
                        savedEntries,
                        totalCount === entries.length
                      ) === version.identifier
                    }
                  />
                )

                return versionItem
              }
              case 'VersionDeleted':
                return (
                  <ShareVersionDeletedItem
                    key={version.identifier}
                    version={version}
                  />
                )
              case 'VersionUnavailable':
                return (
                  <ShareVersionUnavailable
                    key={version.identifier}
                    version={version}
                  />
                )
              default:
                return null
            }
          })}
          {canSeePresences && (
            <SharePresenceItem
              shareIdentifier={shareIdentifier}
              onPresenceReady={scrollToLatestVersion}
            />
          )}
        </InfiniteList>
        {/* And empty div to serve as a scroll anchor */}
        <div ref={versionEndRef} />
      </SidebarSection>
    </>
  )
}
